import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    Typography,
    LinearProgress,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
    Stack,
    Divider,
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    Edit as EditIcon,
    Pause as PauseIcon,
    Stop as StopIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, gql } from '@apollo/client';
import EmailTemplateManager from '../views/dashboard/EmailTemplateSetup';
import { useEmailJobs } from '../contexts/EmailJobContext';

const GET_ALL_EMAIL_TEMPLATES = gql`
    query GetAllEmailTemplates($eventId: String!, $role: ROLE!) {
        getAllEmailTemplates(eventId: $eventId, role: $role) {
            fromEmails {
                id
                role
                templateType
                subject
                body
                createdAt
                modified
            }
            fromTemplates {
                id
                role
                userId
                invitation_subject
                invitation_body
                invitation_is_html
                confirmation_subject
                confirmation_body
                confirmation_is_html
                accept_subject
                accept_body
                accept_is_html
                reject_subject
                reject_body
                reject_is_html
                created_at
                updated_at
            }
        }
    }
`;

const GET_EMAIL_TEMPLATE_BY_ID = gql`
    query GetEmailTemplateById(
        $eventId: String!
        $templateId: String!
        $templateType: String!
        $useTemplateFrom: String!
    ) {
        getEmailTemplateById(
            eventId: $eventId
            templateId: $templateId
            templateType: $templateType
            useTemplateFrom: $useTemplateFrom
        ) {
            id
            role
            templateType
            subject
            body
            isHtml
            createdAt
            modified
        }
    }
`;

const SEND_BULK_EMAIL = gql`
    mutation SendBulkEmail($input: BulkEmailInput!) {
        sendBulkEmail(input: $input) {
            success
            message
            jobId
            total
            sent
            failed
        }
    }
`;

const GET_BULK_EMAIL_PROGRESS = gql`
    query GetBulkEmailProgress($jobId: String!) {
        getBulkEmailProgress(jobId: $jobId) {
            total
            sent
            failed
            currentId
            status
            message
        }
    }
`;

const PAUSE_BULK_EMAIL = gql`
    mutation PauseBulkEmail($jobId: String!) {
        pauseBulkEmail(jobId: $jobId)
    }
`;

const STOP_BULK_EMAIL = gql`
    mutation StopBulkEmail($jobId: String!) {
        stopBulkEmail(jobId: $jobId)
    }
`;

export default function SendEmail({ selectedIds, eventId, role, autoOpen = false, onClose }) {
    const [open, setOpen] = useState(autoOpen);
    const [step, setStep] = useState(0);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateType, setTemplateType] = useState('sender');
    const [useTemplateFrom, setUseTemplateFrom] = useState('emails');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [isHtml, setIsHtml] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { addJob, removeJob } = useEmailJobs();

    // Convert role to match ROLE enum format
    const convertRoleToEnum = roleString => {
        if (!roleString) return roleString;
        let role = roleString.toLowerCase();
        const roleMap = {
            corporates: 'Corporates',
            ctf: 'CTF',
            crisissimulation: 'CrisisSimulation',
            hackathon: 'Hackathon',
            daypass: 'DayPass',
            certificate: 'Certificate',
            student: 'Student',
            meeting: 'Meeting',
            vendors: 'Vendors',
            board_members: 'Board_Members',
            oshrm_board_members: 'OSHRM_Board_Members',
            nama_delegate: 'nama_delegate',
        };
        return roleMap[role] || role;
    };

    const enumRole = convertRoleToEnum(role);

    const { data: templatesData, loading: templatesLoading } = useQuery(GET_ALL_EMAIL_TEMPLATES, {
        variables: { eventId, role: enumRole },
        skip: !open || !eventId || !role,
    });

    const { data: templateData, loading: templateLoading } = useQuery(GET_EMAIL_TEMPLATE_BY_ID, {
        variables: {
            eventId,
            templateId: selectedTemplate?.id?.toString() || '',
            templateType: templateType,
            useTemplateFrom: useTemplateFrom,
        },
        skip: !selectedTemplate || !editMode,
        onCompleted: data => {
            if (data?.getEmailTemplateById) {
                const template = data.getEmailTemplateById;
                setSubject(template.subject || '');
                setBody(template.body || '');
                setIsHtml(template.isHtml !== undefined ? template.isHtml : true);
            }
        },
    });

    // Note: We don't poll here anymore - the context handles it
    // This query is only for the dialog display
    const {
        data: progressData,
        loading: progressLoading,
        startPolling,
        stopPolling,
    } = useQuery(GET_BULK_EMAIL_PROGRESS, {
        variables: { jobId },
        skip: !jobId || !open, // Only poll when dialog is open
        pollInterval: open && jobId ? 1000 : 0,
    });

    const [sendBulkEmail] = useMutation(SEND_BULK_EMAIL, {
        onCompleted: data => {
            const newJobId = data.sendBulkEmail.jobId;
            setJobId(newJobId);
            // Add job to global context for background tracking
            addJob(newJobId, {
                role: role,
                eventId: eventId,
                subject: subject,
            });
            startPolling(1000);
            setStep(2);
        },
        onError: error => {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        },
    });

    const [pauseBulkEmail] = useMutation(PAUSE_BULK_EMAIL);
    const [stopBulkEmail] = useMutation(STOP_BULK_EMAIL);

    useEffect(() => {
        if (progressData?.getBulkEmailProgress?.status === 'completed') {
            // Don't stop polling - let the context handle it
            // The floating indicator will show the completion status
            setSnackbar({
                open: true,
                message: `Email sending completed! Sent: ${progressData.getBulkEmailProgress.sent}, Failed: ${progressData.getBulkEmailProgress.failed}`,
                severity: 'success',
            });
        }
    }, [progressData]);

    const handleClose = () => {
        setOpen(false);
        setStep(0);
        setSelectedTemplate(null);
        // Don't clear jobId or stop polling - let it continue in background
        // The floating indicator will continue to show progress
        // Only stop polling for the dialog, but context continues tracking
        if (jobId) {
            stopPolling(); // Stop dialog polling, but context continues
        }
        if (onClose) onClose();
    };

    const handleTemplateSelect = (template, from) => {
        setSelectedTemplate(template);
        setUseTemplateFrom(from);
        if (from === 'emails') {
            setTemplateType(template.templateType);
            setSubject(template.subject);
            setBody(template.body);
            setIsHtml(true);
        } else {
            if (template.templateType) {
                setTemplateType(template.templateType);
                setSubject(template.subject);
                setBody(template.body);
                setIsHtml(template.isHtml !== undefined ? template.isHtml : true);
            }
        }
        setEditMode(false);
        setStep(1);
    };

    const handleEditTemplate = () => {
        setEditMode(true);
    };

    const handleSend = () => {
        sendBulkEmail({
            variables: {
                input: {
                    eventId,
                    role: enumRole,
                    templateId: selectedTemplate?.id?.toString(),
                    templateType: templateType,
                    subject,
                    body,
                    isHtml,
                    selectedIds: selectedIds.map(id => (typeof id === 'object' ? id.id : id)),
                    useTemplateFrom,
                },
            },
        });
    };

    const handlePause = () => {
        pauseBulkEmail({ variables: { jobId } });
    };

    const handleStop = () => {
        stopBulkEmail({ variables: { jobId } });
        stopPolling();
        setSnackbar({ open: true, message: 'Email sending stopped', severity: 'warning' });
    };

    const progress = progressData?.getBulkEmailProgress;
    const progressPercent = progress ? (progress.sent / progress.total) * 100 : 0;

    return (
        <>
            <Dialog fullWidth maxWidth='md' open={open} onClose={handleClose}>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant='h6'>Send Email ({selectedIds.length} recipients)</Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Stepper activeStep={step} sx={{ mb: 3, mt: 2 }}>
                        <Step>
                            <StepLabel>Select Template</StepLabel>
                        </Step>
                        <Step>
                            <StepLabel>Review & Edit</StepLabel>
                        </Step>
                        <Step>
                            <StepLabel>Sending</StepLabel>
                        </Step>
                    </Stepper>

                    {step === 0 && (
                        <Box>
                            {templatesLoading ? (
                                <LinearProgress />
                            ) : (
                                <TemplateSelector
                                    templatesData={templatesData?.getAllEmailTemplates}
                                    onSelectTemplate={handleTemplateSelect}
                                />
                            )}
                        </Box>
                    )}

                    {step === 1 && (
                        <Box>
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        <Typography variant='h6'>Selected Template</Typography>
                                        <Button
                                            variant='outlined'
                                            size='small'
                                            startIcon={<EditIcon />}
                                            onClick={handleEditTemplate}
                                        >
                                            Edit Content
                                        </Button>
                                    </Box>
                                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                                        Subject: {subject}
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />
                                    {editMode ? (
                                        <EmailTemplateManager
                                            contextId={eventId}
                                            templateType={templateType}
                                            eventId={eventId}
                                            role={enumRole}
                                            initialSubject={subject}
                                            initialBody={body}
                                            initialIsHtml={isHtml}
                                            onSave={data => {
                                                setSubject(data.subject);
                                                setBody(data.body);
                                                setIsHtml(data.isHtml);
                                                setEditMode(false);
                                            }}
                                            hideSaveButton={true}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                bgcolor: 'grey.50',
                                            }}
                                            dangerouslySetInnerHTML={{ __html: body }}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                            <Alert severity='info' sx={{ mb: 2 }}>
                                You can edit the content above. Changes will only apply to this email send, not the
                                template.
                            </Alert>
                        </Box>
                    )}

                    {step === 2 && progress && (
                        <Box>
                            <Card>
                                <CardContent>
                                    <Typography variant='h6' gutterBottom>
                                        Sending Emails...
                                    </Typography>
                                    <LinearProgress
                                        variant='determinate'
                                        value={progressPercent}
                                        sx={{ height: 10, borderRadius: 5, mb: 2 }}
                                    />
                                    <Typography variant='body2' color='text.secondary' gutterBottom>
                                        {progress.message} ({progress.sent}/{progress.total})
                                    </Typography>
                                    {progress.currentId && (
                                        <Typography variant='caption' color='text.secondary'>
                                            Current: {progress.currentId}
                                        </Typography>
                                    )}
                                    <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                                        <Button
                                            variant='outlined'
                                            startIcon={<PauseIcon />}
                                            onClick={handlePause}
                                            disabled={progress.status !== 'sending'}
                                        >
                                            Pause
                                        </Button>
                                        <Button
                                            variant='outlined'
                                            color='error'
                                            startIcon={<StopIcon />}
                                            onClick={handleStop}
                                            disabled={progress.status === 'completed' || progress.status === 'stopped'}
                                        >
                                            Stop
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {step === 0 && <Button onClick={handleClose}>Cancel</Button>}
                    {step === 1 && (
                        <>
                            <Button onClick={() => setStep(0)}>Back</Button>
                            <Button onClick={handleClose}>Cancel</Button>
                            <Button
                                variant='contained'
                                startIcon={<SendIcon />}
                                onClick={handleSend}
                                disabled={!subject || !body}
                            >
                                Send Email
                            </Button>
                        </>
                    )}
                    {step === 2 && progress?.status === 'completed' && (
                        <Button variant='contained' onClick={handleClose} startIcon={<CheckCircleIcon />}>
                            Done
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {selectedIds.length > 0 && !autoOpen && (
                <Button variant='contained' color='error' onClick={() => setOpen(true)}>
                    Send Mail ({selectedIds.length})
                </Button>
            )}
        </>
    );
}

function TemplateSelector({ templatesData, onSelectTemplate }) {
    const { fromEmails = [], fromTemplates = [] } = templatesData || {};

    const getTemplateTypeLabel = type => {
        const labels = {
            sender: 'Confirmation', // If only one template, sender = confirmation
            accept: 'Accept',
            reject: 'Reject',
            upgrade: 'Upgrade',
            invitation: 'Invitation',
            confirmation: 'Confirmation',
            pending: 'Pending',
        };
        return labels[type] || type;
    };

    const getTemplateTypeColor = type => {
        const colors = {
            sender: 'secondary', // Confirmation color
            accept: 'success',
            reject: 'error',
            upgrade: 'warning',
            invitation: 'info',
            confirmation: 'secondary',
            pending: 'warning',
        };
        return colors[type] || 'default';
    };

    // Check if there's only one email template - if so, treat sender as confirmation
    const hasOnlyOneEmailTemplate = fromEmails.length === 1 && fromEmails[0]?.templateType === 'sender';

    // Process email templates - preserve original type for backend, but use displayType for UI
    const processedEmails = fromEmails.map(template => {
        if (hasOnlyOneEmailTemplate && template.templateType === 'sender') {
            return { ...template, displayType: 'confirmation', originalType: 'sender' };
        }
        return { ...template, displayType: template.templateType, originalType: template.templateType };
    });

    // Group templates by display type for better organization
    const groupedEmails = processedEmails.reduce((acc, template) => {
        const displayType = template.displayType || template.templateType;
        if (!acc[displayType]) {
            acc[displayType] = [];
        }
        acc[displayType].push(template);
        return acc;
    }, {});

    return (
        <Box>
            {fromEmails.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant='h6' gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                        Email Configuration Templates
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        Templates from the role's email configuration (Sender, Accept, Reject, Upgrade)
                    </Typography>
                    {Object.keys(groupedEmails).length > 0 ? (
                        Object.keys(groupedEmails).map(templateType => (
                            <Box key={templateType} sx={{ mb: 3 }}>
                                <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                                    {getTemplateTypeLabel(templateType)} Templates
                                </Typography>
                                <Grid container spacing={2}>
                                    {groupedEmails[templateType].map((template, index) => {
                                        // Use displayType for chip label, but keep originalType for backend
                                        const displayType = template.displayType || template.templateType;
                                        const originalType = template.originalType || template.templateType;

                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                                lg={4}
                                                key={`${template.id}-${template.templateType}-${index}`}
                                            >
                                                <Card
                                                    sx={{
                                                        cursor: 'pointer',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 4,
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        // When selecting, use original template type for backend
                                                        const templateToSend = {
                                                            ...template,
                                                            templateType: originalType,
                                                        };
                                                        onSelectTemplate(templateToSend, 'emails');
                                                    }}
                                                >
                                                    <CardContent sx={{ flexGrow: 1 }}>
                                                        <Chip
                                                            label={getTemplateTypeLabel(displayType)}
                                                            color={getTemplateTypeColor(displayType)}
                                                            size='small'
                                                            sx={{ mb: 1.5 }}
                                                        />
                                                        <Typography variant='h6' noWrap gutterBottom>
                                                            {template.subject || 'No Subject'}
                                                        </Typography>
                                                        <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical',
                                                                minHeight: 60,
                                                            }}
                                                        >
                                                            {template.body?.replace(/<[^>]*>/g, '').substring(0, 150) ||
                                                                'No content available'}
                                                            {template.body?.replace(/<[^>]*>/g, '').length > 150 &&
                                                                '...'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        ))
                    ) : (
                        <Alert severity='info'>No email configuration templates available.</Alert>
                    )}
                </Box>
            )}

            {fromTemplates.length > 0 && (
                <Box>
                    <Typography variant='h6' gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                        Custom Email Templates
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        Templates from the email_templates table (Invitation, Confirmation, Accept, Reject)
                    </Typography>
                    <Grid container spacing={2}>
                        {fromTemplates.map((template, templateIndex) => {
                            const templateTypes = [
                                {
                                    type: 'invitation',
                                    subject: template.invitation_subject,
                                    body: template.invitation_body,
                                    isHtml: template.invitation_is_html,
                                },
                                {
                                    type: 'confirmation',
                                    subject: template.confirmation_subject,
                                    body: template.confirmation_body,
                                    isHtml: template.confirmation_is_html,
                                },
                                {
                                    type: 'accept',
                                    subject: template.accept_subject,
                                    body: template.accept_body,
                                    isHtml: template.accept_is_html,
                                },
                                {
                                    type: 'reject',
                                    subject: template.reject_subject,
                                    body: template.reject_body,
                                    isHtml: template.reject_is_html,
                                },
                            ].filter(t => t.subject && t.body);

                            return templateTypes.map((t, typeIndex) => (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={4}
                                    key={`${template.id}-${t.type}-${templateIndex}-${typeIndex}`}
                                >
                                    <Card
                                        sx={{
                                            cursor: 'pointer',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 4,
                                            },
                                        }}
                                        onClick={() =>
                                            onSelectTemplate({ ...template, templateType: t.type, ...t }, 'templates')
                                        }
                                    >
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Chip
                                                label={getTemplateTypeLabel(t.type)}
                                                color={getTemplateTypeColor(t.type)}
                                                size='small'
                                                sx={{ mb: 1.5 }}
                                            />
                                            <Typography variant='h6' noWrap gutterBottom>
                                                {t.subject || 'No Subject'}
                                            </Typography>
                                            <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    minHeight: 60,
                                                }}
                                            >
                                                {t.body?.replace(/<[^>]*>/g, '').substring(0, 150) ||
                                                    'No content available'}
                                                {t.body?.replace(/<[^>]*>/g, '').length > 150 && '...'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ));
                        })}
                    </Grid>
                </Box>
            )}

            {fromEmails.length === 0 && fromTemplates.length === 0 && (
                <Alert severity='info'>No templates available. Please create templates first.</Alert>
            )}
        </Box>
    );
}
